import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import * as api from '../api/api';
import MultiMetricChart from './MultiMetricChart';
import { formatDisplayDate } from '../utils/DateUtil';
import './ActivityGraph.css';

const METRICS = {
    distance: { label: 'Distance', colour: '#1769aa', unit: 'km' },
    time: { label: 'Time', colour: '#327fa7', unit: '' },
    pace: { label: 'Pace', colour: '#1769aa', unit: 'min/km', reverse: true },
    gap: { label: 'Grade adjusted pace', colour: '#7a4ba6', unit: 'min/km', reverse: true },
    avg_heartrate: { label: 'Average heart rate', colour: '#c43c3c', unit: 'bpm' },
    max_heartrate: { label: 'Maximum heart rate', colour: '#c43c3c', unit: 'bpm' },
    elevation_gain: { label: 'Elevation gain', colour: '#73777a', unit: 'm' },
    grade_percent: { label: 'Grade', colour: '#258277', unit: '%' },
    cadence: { label: 'Cadence', colour: '#d35f8d', unit: 'spm' },
};
const ACTIVITY_TYPES = ['RUN', 'RIDE', 'WALK', 'HIKE', 'SWIM', 'WORKOUT', 'WEIGHTTRAINING'];
const GROUPINGS = ['year', 'month', 'week', 'day'];
const AGGREGATIONS = ['median', 'mean', 'max', 'total'];
const formatAggregation = (value) => value.charAt(0).toUpperCase() + value.slice(1);
const formatActivityType = (value) => value === 'WEIGHTTRAINING'
    ? 'Weight training'
    : value.charAt(0) + value.slice(1).toLowerCase();
const TODAY = new Date().toISOString().slice(0, 10);
const ONE_MONTH_AGO = new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10);
const formatDate = (value) => formatDisplayDate(`${value}T00:00:00`);
// Deliberately not the shared ddth-mmm-yyyy format - these are compact chart
// axis ticks, where the ordinal suffix and (for day/week ticks) the year
// would just add width without adding information.
const formatXAxisLabel = (point, grouping) => {
    const date = new Date(`${point.start}T00:00:00`);
    if (grouping === 'year') return String(date.getFullYear());
    if (grouping === 'month') return date.toLocaleDateString('en-GB', { month: 'short' });
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const formatValue = (value, metric) => {
    if (metric === 'time') {
        const totalMinutes = Math.round(value / 60);
        return `${Math.floor(totalMinutes / 60)}h ${String(totalMinutes % 60).padStart(2, '0')}m`;
    }
    if (metric === 'pace' || metric === 'gap') {
        const totalSeconds = Math.round(value * 60);
        return `${Math.floor(totalSeconds / 60)}:${String(totalSeconds % 60).padStart(2, '0')}`;
    }
    if (metric === 'grade_percent') return value.toFixed(1);
    return Math.round(value).toLocaleString();
};
const formatValueWithUnit = (value, metric) => `${formatValue(value, metric)}${METRICS[metric].unit ? ` ${METRICS[metric].unit}` : ''}`;
const parseDateParam = (value) => value ? new Date(`${value}T00:00:00`) : null;
const formatDateParam = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const ActivityGraph = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const startDate = searchParams.get('start') || ONE_MONTH_AGO;
    const endDate = searchParams.get('end') || TODAY;
    const grouping = GROUPINGS.includes(searchParams.get('group')) ? searchParams.get('group') : 'day';
    const aggregationParam = searchParams.get('aggregation');
    const aggregation = aggregationParam === 'mode'
        ? 'max'
        : AGGREGATIONS.includes(aggregationParam) ? aggregationParam : 'total';
    const metricsParam = searchParams.has('metrics')
        ? searchParams.get('metrics')
        : searchParams.get('metric') || 'distance';
    const requestedMetrics = metricsParam.split(',').filter((metric) => METRICS[metric]);
    const selectedMetrics = requestedMetrics;
    const activityTypesParam = searchParams.get('activityTypes') || searchParams.get('activity') || 'ALL';
    const selectedActivityTypes = activityTypesParam === 'ALL' ? ['ALL'] : activityTypesParam.split(',').filter((type) => ACTIVITY_TYPES.includes(type));
    const [series, setSeries] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedDay, setSelectedDay] = useState(null);
    const [error, setError] = useState('');
    const graphRequestId = useRef(0);
    const updateGraphParams = (updates) => {
        const nextParams = new URLSearchParams(searchParams);
        Object.entries(updates).forEach(([key, value]) => nextParams.set(key, value));
        setSearchParams(nextParams);
    };
    const toggleMetric = (metric) => {
        const nextMetrics = selectedMetrics.includes(metric)
            ? selectedMetrics.filter((selectedMetric) => selectedMetric !== metric)
            : [...selectedMetrics, metric];
        updateGraphParams({ metrics: nextMetrics.join(',') });
    };
    const toggleActivityType = (activityType) => {
        const nextActivityTypes = selectedActivityTypes.includes('ALL')
            ? [activityType]
            : selectedActivityTypes.includes(activityType)
                ? selectedActivityTypes.filter((selectedType) => selectedType !== activityType)
                : [...selectedActivityTypes, activityType];
        updateGraphParams({ activityTypes: (nextActivityTypes.length ? nextActivityTypes : ['ALL']).join(',') });
    };

    useEffect(() => {
        if (!searchParams.get('start') || !searchParams.get('end') || !searchParams.get('group') || !searchParams.has('metrics') || !searchParams.get('activityTypes') || !searchParams.get('aggregation')) {
            setSearchParams({ start: startDate, end: endDate, group: grouping, metrics: selectedMetrics.join(','), activityTypes: selectedActivityTypes.join(','), aggregation }, { replace: true });
        }
    }, [activityTypesParam, aggregation, endDate, grouping, metricsParam, searchParams, selectedActivityTypes, selectedMetrics, setSearchParams, startDate]);

    useEffect(() => {
        const requestId = graphRequestId.current + 1;
        graphRequestId.current = requestId;
        setSelectedDay(null);
        if (selectedMetrics.length === 0) {
            setSeries([]);
            setError('');
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        api.getActivityGraph({ startDate, endDate, grouping, metrics: selectedMetrics, activityTypes: selectedActivityTypes, aggregation })
            .then(({ data }) => {
                if (graphRequestId.current === requestId) {
                    setSeries(data.series);
                    setError('');
                }
            })
            .catch((e) => {
                if (graphRequestId.current === requestId) {
                    setSeries([]);
                    setError(api.getErrorMessage(e, 'Unable to load graph data.'));
                }
            })
            .finally(() => {
                if (graphRequestId.current === requestId) setIsLoading(false);
            });
    }, [startDate, endDate, grouping, metricsParam, activityTypesParam, aggregation, selectedMetrics.length]);

    const visibleSeries = series.filter((graphSeries) => graphSeries.points.length > 0);
    const points = visibleSeries[0]?.points || [];
    const drillDown = (point) => {
        if (grouping === 'day') {
            setSelectedDay(point);
            return;
        }
        const nextGrouping = GROUPINGS[GROUPINGS.indexOf(grouping) + 1];
        updateGraphParams({ start: point.start, end: point.end, group: nextGrouping });
    };

    return (
        <main className="activity-graph">
            <section className="graph-controls" aria-label="Graph controls">
                <div className="graph-control-row">
                    <label>From<DatePicker
                        className="editor-input"
                        wrapperClassName="date-picker-wrapper"
                        selected={parseDateParam(startDate)}
                        onChange={(date) => date && updateGraphParams({ start: formatDateParam(date) })}
                        maxDate={parseDateParam(endDate)}
                        dateFormat="do MMM yyyy"
                        showPopperArrow={false}
                    /></label>
                    <label>To<DatePicker
                        className="editor-input"
                        wrapperClassName="date-picker-wrapper"
                        selected={parseDateParam(endDate)}
                        onChange={(date) => date && updateGraphParams({ end: formatDateParam(date) })}
                        minDate={parseDateParam(startDate)}
                        maxDate={parseDateParam(TODAY)}
                        dateFormat="do MMM yyyy"
                        showPopperArrow={false}
                    /></label>
                    <label>Aggregate<select value={aggregation} onChange={(event) => updateGraphParams({ aggregation: event.target.value })}>{AGGREGATIONS.map((value) => <option key={value} value={value}>{formatAggregation(value)}</option>)}</select></label>
                    <fieldset className="grouping-options"><legend>Group by</legend>{GROUPINGS.map((value) => <button key={value} type="button" className={grouping === value ? 'selected' : ''} onClick={() => updateGraphParams({ group: value })}>{value}</button>)}</fieldset>
                </div>
                <fieldset className="filter-options"><legend>Metrics</legend><div>{Object.entries(METRICS).map(([value, option]) => <button key={value} type="button" className={selectedMetrics.includes(value) ? 'selected' : ''} aria-pressed={selectedMetrics.includes(value)} onClick={() => toggleMetric(value)}><i style={{ background: option.colour }} />{option.label}</button>)}</div></fieldset>
                <fieldset className="filter-options"><legend>Activities</legend><div><button type="button" className={selectedActivityTypes.includes('ALL') ? 'selected' : ''} aria-pressed={selectedActivityTypes.includes('ALL')} onClick={() => updateGraphParams({ activityTypes: 'ALL' })}>All activities</button>{ACTIVITY_TYPES.map((value) => <button key={value} type="button" className={selectedActivityTypes.includes(value) ? 'selected' : ''} aria-pressed={selectedActivityTypes.includes(value)} onClick={() => toggleActivityType(value)}>{formatActivityType(value)}</button>)}</div></fieldset>
            </section>
            <section className="chart-panel">
                {selectedMetrics.length === 0 ? <p className="graph-message">Select a metric to view your activity graph.</p> : error && points.length === 0 ? <p className="graph-message">{error}</p> : points.length === 0 && !isLoading ? <p className="graph-message">No matching activities with these metrics.</p> : <>
                    <div className="chart-summary">{visibleSeries.map((graphSeries) => <span key={graphSeries.metric}><i style={{ background: METRICS[graphSeries.metric].colour }} />{formatAggregation(aggregation)} {METRICS[graphSeries.metric].label.toLowerCase()} <strong>{formatValue(graphSeries.summary, graphSeries.metric)} <small>{METRICS[graphSeries.metric].unit}</small></strong></span>)}</div>
                    {points.length > 0 && <MultiMetricChart series={series} metricsConfig={METRICS} selectedMetrics={selectedMetrics} formatValueWithUnit={formatValueWithUnit} formatXAxisLabel={(point) => formatXAxisLabel(point, grouping)} formatTooltipLabel={(point) => formatDate(point.start)} onPointClick={drillDown} xDomainStart={startDate} xDomainEnd={endDate} />}
                    {isLoading && <div className="graph-loading-overlay" role="status" aria-label="Loading graph data"><span /></div>}
                    <p className="chart-hint">Drag across the chart to compare an interval. Select a point to {grouping === 'day' ? 'view its activities' : 'drill into its date range'}.</p>
                </>}
            </section>
            {grouping === 'day' && selectedDay && <section className="activity-list"><h2>Activities on {formatDate(selectedDay.start)}</h2>{selectedDay.activities.map((activity) => <Link key={`${activity.id}-${activity.date}`} to={`/activity/${activity.id}`}><strong>{activity.name}</strong><small>{activity.type}</small><div className="activity-metrics">{selectedMetrics.map((metric) => <span key={metric}>{METRICS[metric].label}: <b>{activity.metrics[metric] === null ? '—' : `${formatValue(activity.metrics[metric], metric)} ${METRICS[metric].unit}`}</b></span>)}</div><span aria-hidden="true">›</span></Link>)}</section>}
        </main>
    );
};

export default ActivityGraph;